// Live's handles round the chosen item on the canvas (issue 560): white squares on the corners a
// picture scales by, a bar on a text's right edge, and, just outside each corner, the zone a drag
// turns it from (live draws none there; its cursor shows it). They sit in the item's outline, so
// they turn with it. The drags themselves are the Preview's (`canvas-drag.ts` holds the maths).
import type { CSSProperties, PointerEvent } from "react";

import { CORNER_HANDLES, type Gesture, type Handle } from "./canvas-drag";
import classes from "./canvas-handles.module.css";

/** Where each handle sits on the outline, centred on its corner or edge. */
const HANDLE_PLACE: Record<Handle, CSSProperties> = {
  tl: { left: 0, top: 0 },
  tr: { left: "100%", top: 0 },
  bl: { left: 0, top: "100%" },
  br: { left: "100%", top: "100%" },
  ml: { left: 0, top: "50%" },
  mr: { left: "100%", top: "50%" },
  mt: { left: "50%", top: 0 },
  mb: { left: "50%", top: "100%" },
};

/** Live's resize cursors, by handle. */
const CURSOR: Record<Handle, CSSProperties["cursor"]> = {
  tl: "nwse-resize",
  br: "nwse-resize",
  tr: "nesw-resize",
  bl: "nesw-resize",
  ml: "ew-resize",
  mr: "ew-resize",
  mt: "ns-resize",
  mb: "ns-resize",
};

/** Live's turn zones: 24 px squares reaching outward from each corner (its `rot_*` hit areas). */
const TURN_PLACE: Record<(typeof CORNER_HANDLES)[number], CSSProperties> = {
  tl: { left: -22, top: -22 },
  tr: { left: "calc(100% - 2px)", top: -22 },
  bl: { left: -22, top: "calc(100% - 2px)" },
  br: { left: "calc(100% - 2px)", top: "calc(100% - 2px)" },
};

export function CanvasHandles(props: {
  handles: readonly Handle[];
  turns: boolean;
  onBegin: (gesture: Gesture, event: PointerEvent<HTMLElement>) => void;
}) {
  const begin = (gesture: Gesture) => (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    // The handle's own drag: the frame's press (which picks) does not see it.
    event.stopPropagation();
    event.preventDefault();
    props.onBegin(gesture, event);
  };
  return (
    <>
      {props.turns
        ? CORNER_HANDLES.map((corner) => (
            <span
              key={`turn-${corner}`}
              className={classes.turn}
              style={TURN_PLACE[corner]}
              data-turn={corner}
              onPointerDown={begin("turn")}
            />
          ))
        : null}
      {props.handles.map((handle) => (
        <span
          key={handle}
          className={
            handle === "ml" || handle === "mr" ? classes.edge : handle === "mt" || handle === "mb" ? classes.edgeAcross : classes.corner
          }
          style={{ ...HANDLE_PLACE[handle], cursor: CURSOR[handle] }}
          data-handle={handle}
          onPointerDown={begin(handle)}
        />
      ))}
    </>
  );
}
